import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface WorldElementDto {
  id: string;
  worldId: string;
  parentId?: string | null;
  name: string;
  elementType: string;
  description?: string | null;
  imageUrl?: string | null;
  sequenceOrder: number;
  linkedEntityIds?: string[];
  metadata?: Record<string, unknown> | null;
}

export interface WorldDto {
  id: string;
  name: string;
  description?: string | null;
  elements: WorldElementDto[];
  updatedAt: string;
}

export function useBookWorlds(projectId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<WorldDto[]>({
    enabled: !!projectId,
    queryKey: ['book-worlds', projectId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/projects/${projectId}/worlds`)
        .json<ApiResponse<WorldDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useCreateWorld(projectId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post('api/v2/worlds', { json: { ...input, projectId } })
        .json<ApiResponse<WorldDto>>();
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['book-worlds', projectId],
      }),
  });
}

export function useAddWorldElement(projectId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      worldId: string;
      name: string;
      elementType: string;
      description?: string;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/worlds/${input.worldId}/elements`, {
          json: {
            name: input.name,
            elementType: input.elementType,
            description: input.description,
          },
        })
        .json<ApiResponse<WorldElementDto>>();
      return response.data;
    },
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['world', vars.worldId] });
      void queryClient.invalidateQueries({ queryKey: ['worlds'] });
      if (projectId) {
        void queryClient.invalidateQueries({
          queryKey: ['book-worlds', projectId],
        });
      }
    },
  });
}

export interface WorldSummaryDto {
  id: string;
  projectId?: string | null;
  name: string;
  description?: string | null;
  elementCount: number;
  updatedAt: string;
}

export interface WorldTemplateDto {
  id: string;
  name: string;
  description?: string | null;
  source: string;
  elementTypes: string[];
  tropes: string[];
}

export function useMyWorlds(search?: string) {
  const { getToken } = useAuth();
  return useQuery<WorldSummaryDto[]>({
    queryKey: ['worlds', search ?? ''],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const q = search ? `?search=${encodeURIComponent(search)}` : '';
      const resp = await client
        .get(`api/v2/worlds${q}`)
        .json<ApiResponse<WorldSummaryDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useWorld(id: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<WorldDto>({
    enabled: !!id,
    queryKey: ['world', id],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/worlds/${id}`)
        .json<ApiResponse<WorldDto>>();
      return resp.data;
    },
  });
}

export function useUpdateWorldElement(worldId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      elementType: string;
      description?: string;
      imageUrl?: string | null;
      linkedEntityIds?: string[];
      metadata?: Record<string, unknown>;
    }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/world-elements/${input.id}`, {
        json: {
          name: input.name,
          elementType: input.elementType,
          description: input.description,
          imageUrl: input.imageUrl,
          linkedEntityIds: input.linkedEntityIds ?? [],
          metadata: input.metadata,
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['world', worldId] }),
  });
}

export function useUpdateWorldElementImage(worldId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      elementId: string;
      imageUrl: string | null;
    }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/world-elements/${input.elementId}/image`, {
        json: { imageUrl: input.imageUrl },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['world', worldId] }),
  });
}

export function useUpdateWorld(id: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/worlds/${id}`, { json: input });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['world', id] });
      void qc.invalidateQueries({ queryKey: ['worlds'] });
    },
  });
}

export function useReorderWorldElement(worldId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; newSequenceOrder: number }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/world-elements/${input.id}/reorder`, {
        json: { newSequenceOrder: input.newSequenceOrder },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['world', worldId] }),
  });
}

export function useReparentWorldElement(worldId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; newParentId: string | null }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/world-elements/${input.id}/reparent`, {
        json: { newParentId: input.newParentId },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['world', worldId] }),
  });
}

export function useDeleteWorldElement(worldId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/world-elements/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['world', worldId] }),
  });
}

export function useWorldTemplates() {
  const { getToken } = useAuth();
  return useQuery<WorldTemplateDto[]>({
    queryKey: ['world-templates'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/worlds/templates')
        .json<ApiResponse<WorldTemplateDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useCreateWorldFromTemplate() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      name: string;
      projectId?: string | null;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/worlds/from-template/${input.templateId}`, {
          json: { name: input.name, projectId: input.projectId },
        })
        .json<ApiResponse<WorldDto>>();
      return resp.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['worlds'] }),
  });
}

export function useAssociateWorldToBook() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { bookId: string; worldId: string }) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/books/${input.bookId}/worlds/${input.worldId}`);
    },
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['book-worlds', vars.bookId] }),
  });
}

export function useDisassociateWorldFromBook() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { bookId: string; worldId: string }) => {
      const client = createApiClient(getToken);
      await client.delete(
        `api/v2/books/${input.bookId}/worlds/${input.worldId}`,
      );
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['book-worlds', vars.bookId] });
      void qc.invalidateQueries({ queryKey: ['world', vars.worldId] });
      void qc.invalidateQueries({ queryKey: ['worlds'] });
    },
  });
}

export function useDeleteWorld() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/worlds/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['worlds'] });
      void qc.invalidateQueries({ queryKey: ['book-worlds'] });
    },
  });
}

export const WORLD_ELEMENT_TYPES: Array<{ value: string; label: string }> = [
  { value: 'magic_system', label: 'Magic system' },
  { value: 'ability', label: 'Ability' },
  { value: 'being', label: 'Being' },
  { value: 'creature', label: 'Creature' },
  { value: 'country', label: 'Country' },
  { value: 'culture', label: 'Culture' },
  { value: 'deity', label: 'Deity' },
  { value: 'ecosystem', label: 'Ecosystem' },
  { value: 'economy', label: 'Economy' },
  { value: 'government', label: 'Government' },
  { value: 'history', label: 'History' },
  { value: 'language', label: 'Language' },
  { value: 'law', label: 'Law' },
  { value: 'location', label: 'Location' },
  { value: 'magic', label: 'Magic' },
  { value: 'mythology', label: 'Mythology' },
  { value: 'religion', label: 'Religion' },
  { value: 'society', label: 'Society' },
  { value: 'technology', label: 'Technology' },
  { value: 'weapon', label: 'Weapon' },
  { value: 'spell', label: 'Spell' },
  { value: 'faction', label: 'Faction' },
  { value: 'potion', label: 'Potion' },
];
